import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, X } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortfolioPage = () => {
  const [portfolio, setPortfolio] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [portRes, catRes] = await Promise.all([
          axios.get(`${API}/portfolio`),
          axios.get(`${API}/categories`)
        ]);
        setPortfolio(portRes.data);
        setCategories(catRes.data);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredPortfolio = selectedCategory
    ? portfolio.filter(item => item.category_id === selectedCategory)
    : portfolio;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-handcraft">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-charcoal transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-charcoal">Portfolio</span>
          </nav>

          <div className="text-center max-w-2xl mx-auto">
            <p className="font-handwritten text-2xl text-accent mb-2">Our Work</p>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">Portfolio</h1>
            <p className="text-muted-foreground">
              Explore our past projects and creations. Each piece showcases our dedication to craftsmanship and attention to detail.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-cream border-y border-sand py-4">
        <div className="container-handcraft">
          <div className="flex items-center gap-4 overflow-x-auto pb-2">
            <Button
              variant={!selectedCategory ? 'default' : 'ghost'}
              className={`rounded-full whitespace-nowrap ${!selectedCategory ? 'bg-charcoal text-cream' : ''}`}
              onClick={() => setSelectedCategory(null)}
              data-testid="portfolio-filter-all"
            >
              All Work
            </Button>
            {categories.map(category => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'ghost'}
                className={`rounded-full whitespace-nowrap ${selectedCategory === category.id ? 'bg-charcoal text-cream' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
                data-testid={`portfolio-filter-${category.slug}`}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="section-spacing">
        <div className="container-handcraft">
          {loading ? (
            <div className="gallery-grid">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-square rounded-2xl skeleton" />
              ))}
            </div>
          ) : filteredPortfolio.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-handwritten text-3xl text-accent mb-4">Coming Soon</p>
              <h2 className="font-serif text-2xl text-charcoal mb-4">No portfolio items yet</h2>
              <p className="text-muted-foreground">
                Check back soon for our latest work!
              </p>
            </div>
          ) : (
            <div className="gallery-grid">
              {filteredPortfolio.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="group cursor-pointer"
                  onClick={() => setLightbox(item)}
                  data-testid={`portfolio-item-${item.id}`}
                >
                  <div className="relative aspect-square rounded-2xl overflow-hidden bg-sand img-zoom">
                    <img
                      src={item.images?.[0] || 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800'}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/40 transition-colors flex items-end">
                      <div className="p-6 translate-y-full group-hover:translate-y-0 transition-transform">
                        <h3 className="font-serif text-xl text-white">{item.title}</h3>
                        <p className="text-white/80 text-sm line-clamp-2">{item.description}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div 
          className="fixed inset-0 z-50 bg-charcoal/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full"
            onClick={() => setLightbox(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            <img
              src={lightbox.images?.[0] || 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1200'}
              alt={lightbox.title}
              className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center text-white">
              <h3 className="font-serif text-2xl">{lightbox.title}</h3>
              <p className="text-white/70 mt-2">{lightbox.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="section-spacing bg-white">
        <div className="container-handcraft text-center">
          <p className="font-handwritten text-2xl text-accent mb-4">Inspired?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-charcoal mb-6">
            Let's Create Something Together
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">
            Have a custom project in mind? We'd love to hear about it and bring your vision to life.
          </p>
          <Link to="/contact">
            <Button className="btn-handcraft bg-charcoal text-cream">
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PortfolioPage;
